import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Field, Form } from "formik";
import * as yup from "yup";
import { request } from "../../../../utils/Request";
import { Col, Row } from "react-bootstrap";
import { InputField } from "../../../../components/Shared/InputField";
import { Sidebar } from "primereact/sidebar";
import { debounce, find, get, has, includes, isEmpty, trim } from "lodash";
import { Button } from "primereact/button";
import { InputSwitchField } from "../../../../components/Shared/InputSwitchField";
import InputDateTime from "../../../../components/Shared/InputDateTime";
import moment from "moment-timezone";
import { InputSelectField } from "../../../../components/Shared/InputSelectField";
import { InputSingleSelectField } from "../../../../components/Shared/InputSingleSelectField";
import { Column } from "primereact/column";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import { formatDate } from "../../../../utils/Helper";
import { DataTable } from "primereact/datatable";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    params: any;
    toastify: any;
    onClose?: any;
    operationCancel?: any;
}

type TInputForm = {
    id?: any,
    travel_order_number?: any,
    transport_vehicle_id?: any,
    legal_entity_id?: any,
    start_mileage?: any,
    end_mileage?: any,
    total_kilometers?: any,
    departure_date?: any,
    return_date?: any,
    revenue?: any,
    coefficient?: any,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        travel_order_number: yup.string().required(t('the field is required.', { field: t('travel order number') })).nullable(),
        transport_vehicle_id: yup.string().required(t('the field is required.', { field: t('transport vehicle') })).nullable(),
        legal_entity_id: yup.string().nullable(),
        start_mileage: yup.string().required(t('the field is required.', { field: t('start mileage') })).nullable(),
        end_mileage: yup.string().required(t('the field is required.', { field: t('end mileage') })).nullable(),
        total_kilometers: yup.string().required(t('the field is required.', { field: t('total kilometers') })).nullable(),
        departure_date: yup.string().required(t('the field is required.', { field: t('departure date') })).nullable(),
        return_date: yup.string().required(t('the field is required.', { field: t('return date') })).nullable(),
        revenue: yup.number().nullable(),
        coefficient: yup.number().nullable(),
    });
};

const DEFAULT_FORM_STATE = {
    id: null,
    travel_order_number: null,
    transport_vehicle_id: null,
    legal_entity_id: null,
    start_mileage: null,
    end_mileage: null,
    total_kilometers: 0,
    revenue: 0,
    coefficient: 0,
    departure_date: null,
    return_date: null,
};

function TravelOrderForm(props: IFormProps) {
    const { t, toastify, params, dropdowns, operationCancel } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);
    const [entity, setEntity] = useState<any>(null);

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            const response = await request({
                method: ( !payloads.id ? 'POST' : 'PATCH' ),
                url: ( !payloads.id ? `/suppliers/travel/orders` : `/suppliers/travel/orders/${ payloads.id }` ),
                data: {
                    ...payloads,
                },
            });

            if ( payloads.id ) {
                toastify(t("record updated", { item: t('travel order') }), "success");
            } else {
                toastify(t("record added", { item: t('travel order') }), "info");
            }

            resetForm();
            onClose();
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
            } else {
                toastify(t("server error"), "error");

            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;

        if ( base ) {
            base.resetForm();
        }
    };

    const onClose = () => {
        setPanelState(false);
        const base: any = formRef.current;

        if ( base ) {
            base.resetForm();
        }

        if ( props.onClose ) {
            props.onClose(
                !includes(['edit'], get(params, ['operation']))
            );
        }
    };

    const onEdit = async (id: string): Promise<void> => {
        try {
            const response = await request({
                url: `/suppliers/travel/orders/${ id }`
            });

            const { data } = response.data;
            const { transport_vehicle, legal_entity } = data;
            delete data.transport_vehicle;
            delete data.legal_entity;

            const formData: TInputForm = {
                ...data,
                transport_vehicle_id: get(transport_vehicle, ['id']),
                legal_entity_id: get(legal_entity, ['id']),
            };

            setEntity(data);
            setInitFormState(formData);
            setPanelState(true);
        } catch (error: any) {
            toastify(t("server error"), "error");
        }
    };

    const onCalculateCoefficient = () => {
        const base: any = get(formRef.current, ['values']);
        let kms: number = base.total_kilometers;
        let revenue: number = base.revenue;
        let coefficient: number = base.coefficient;

        if(
            !isEmpty(base.start_mileage)
            && !isEmpty(base.end_mileage)
            && Number(base.start_mileage) > 0
            && Number(base.end_mileage) > 0
            && ( Number(base.end_mileage) - Number(base.start_mileage) ) > 0
        ){
            kms = Number((Number(base.end_mileage) - Number(base.start_mileage)).toFixed(2))
        }

        if ( kms > 0 && Number(revenue) > 0 ) {
            coefficient = Number(( Number(revenue) / Number(kms) ).toFixed(2));
        }

        setInitFormState({
            ...base,
            coefficient: coefficient
        });
    };

    const onCalculateTotalKm = () => {
        const base: any = get(formRef.current, ['values']);

        if(
            !isEmpty(base.start_mileage)
            && !isEmpty(base.end_mileage)
            && Number(base.start_mileage) > 0
            && Number(base.end_mileage) > 0
            && ( Number(base.end_mileage) - Number(base.start_mileage) ) > 0
        ){
            setInitFormState({
                ...base,
                total_kilometers: Number((Number(base.end_mileage) - Number(base.start_mileage)).toFixed(2))
            });
        } else {
            if ( base.total_kilometers != 0 ) {
                setInitFormState({
                    ...base,
                    total_kilometers: 0
                });
            }
        }

        // onCalculateCoefficient();
    };

    useEffect(() => {
        if ( includes(['add'], get(params, ['operation'])) ) {
            setPanelState(true);
        }

        if ( includes(['edit'], get(params, ['operation'])) ) {
            onEdit(params.operationId);
        }
    }, [params]);

    const onChangeTransportVehicle = (item: any) => {
        const value: any = find(dropdowns.transportVehicle, { key: item.value });
        const base: any = get(formRef.current, ['values']);
        let legalEntityId = null;

        if ( !isEmpty(value.id) ) {
            legalEntityId = value.truck_owner_id
        }

        setInitFormState({
            ...base,
            transport_vehicle_id: item.value,
            legal_entity_id: legalEntityId
        });
    }

    const debouncedRevenueChange = useCallback(
        debounce(() => {
            onCalculateCoefficient()
        }, 300), // 300ms debounce
        []
    );

    const debouncedKmChange = useCallback(
        debounce(() => onCalculateTotalKm(), 300), // 300ms debounce
        []
    );

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('travel order') + ' ' + t('action') }</h5>
                            <p>{ t("using this form you can add or update the record") }</p>
                        </div>
                    }
                    visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                    style={ { width: '800px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 }>
                                            <Field component={ InputField }
                                                   name="travel_order_number"
                                                   label={ t("travel order number") }
                                                   placeholder={ t("enter") + ' ' + t("travel order number").toLowerCase() }
                                                   rounded
                                                   isRequired
                                                   autoFocus />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `departure_date` }
                                                type={ "datetime" }
                                                label={ t("departure date") }
                                                isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `return_date` }
                                                type={ "datetime" }
                                                label={ t("return date") }
                                                isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSingleSelectField }
                                                   options={ dropdowns.transportVehicle }
                                                   onChange={ onChangeTransportVehicle }
                                                   name="transport_vehicle_id"
                                                   placeholder={ t("select") + ' ' + t("transport vehicle") }
                                                   isRequired
                                                   label={ t('transport vehicle') } />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSingleSelectField }
                                                   options={ dropdowns.legalEntities }
                                                   name="legal_entity_id"
                                                   placeholder={ t("select") + ' ' + t("legal entity") }
                                                   label={ t('legal entity') } />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="start_mileage"
                                                   label={ t("start mileage") }
                                                   placeholder={ t("enter") + ' ' + t("start mileage").toLowerCase() }
                                                   onBlur={ onCalculateTotalKm }
                                                   onInput={(e: any) => debouncedKmChange()}
                                                   rounded
                                                   isRequired/>
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="end_mileage"
                                                   label={ t("end mileage") }
                                                   placeholder={ t("enter") + ' ' + t("end mileage").toLowerCase() }
                                                   onBlur={ onCalculateTotalKm }
                                                   onInput={(e: any) => debouncedKmChange()}
                                                   rounded
                                                   isRequired />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="total_kilometers"
                                                   label={ t("total kilometers") }
                                                   onBlur={ onCalculateCoefficient }
                                                   placeholder={ t("enter") + ' ' + t("total kilometers").toLowerCase() }
                                                   disabled
                                                   rounded/>
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="revenue"
                                                   label={ t("revenue") }
                                                   placeholder={ t("enter") + ' ' + t("revenue").toLowerCase() }
                                                   onBlur={ onCalculateCoefficient }
                                                   onInput={(e: any) => debouncedRevenueChange()}
                                                   rounded/>
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="coefficient"
                                                   label={ t("coefficient") }
                                                   placeholder={ t("enter") + ' ' + t("coefficient").toLowerCase() }
                                                   rounded/>
                                        </Col>

                                        {
                                            (entity && entity.load_orders) &&
                                          <>
                                              <Col sm={ 12 } className={"mt-2 mb-4"}>
                                                  <Row>
                                                      <Col sm={ 6 }>
                                                          <h5 className={"mb-0"}>{ t('connected loads') }</h5>
                                                      </Col>
                                                      <Col sm={ 6 } className={"text-end"}>
                                                          {/*<Button type={ "submit" } disabled={ props.isSubmitting }*/}
                                                          {/*        size={ "small" } outlined*/}
                                                          {/*        tooltipOptions={ { position: 'top' } }*/}
                                                          {/*>*/}
                                                          {/*    <i className="pi pi-calculator me-2" />*/}
                                                          {/*    { t("populate total") }*/}
                                                          {/*</Button>*/}
                                                      </Col>
                                                  </Row>
                                                  <hr />
                                                  <DataTable value={ entity.load_orders } editMode="row" dataKey="id">
                                                      <Column field="load_type" header={ t('load type') }
                                                              body={ (rowData: any) => rowData.load_type } />
                                                      <Column field="load_number" header={ t('load number') }
                                                              body={ (rowData: any) => rowData.load_number } />
                                                      <Column field="route_title" header={ t('route') }
                                                              body={ (rowData: any) => rowData.route_title } />
                                                      <Column field="total_km" header={ t('total km') }
                                                              body={ (rowData: any) => rowData.total_km } />
                                                      <Column field="start_meter" header={ t('start meter') }
                                                              body={ (rowData: any) => rowData.start_meter } />
                                                      <Column field="end_meter" header={ t('end meter') }
                                                              body={ (rowData: any) => rowData.end_meter } />
                                                  </DataTable>
                                              </Col>
                                          </>
                                        }
                                    </Row>
                                    <div className="filter-card-footer"
                                         style={ { width: '785px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("to data save into database") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-save me-2" />
                                            { t("let's save") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onReset }
                                                tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                size={ "small" } severity={ "warning" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                                onClick={ onClose }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("cancel") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

TravelOrderForm.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(TravelOrderForm);
